import React, { FC, useState, ChangeEvent, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { message, Tag, Input, InputRef, Avatar } from 'antd';
import './TagInput.scss';
import { OrganizationIcon } from '../../assets/CustomIcons/OrganizationIcon';
import { WorldIcon } from '../../assets/CustomIcons/WorldIcon';
import colorTokens from '../../constants/design_system/colors';
import { getUserInitials } from '../../common/userHelper';
import CustomTooltip from '../UI/Tooltip/CustomTooltip';

type TagInputProps = {
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  value: string[];
  emailType: (email: string) => EmailType;
  onChange: (value: string[]) => void;

  onTextContentChange: (value: string) => void;

  // Optional: Control the text input value from parent
  inputValue?: string;
};

export enum EmailType {
  sameDomain,
  differentDomain,
  orgUser,
}

export interface TagInputRef {
  focus: () => void;
}

const TagInput = forwardRef<TagInputRef, TagInputProps>(
  ({ value = [], emailType, onChange, placeholder, onTextContentChange, inputValue, ...props }, ref) => {
    const [content, setContent] = useState<string>();
    const inputRef = useRef<InputRef>(null);

    // Use controlled inputValue if provided, otherwise use internal content state
    const currentInputValue = inputValue ?? content;

    const [messageApi, contextHolder] = message.useMessage();

    // Expose focus method to parent component
    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
      },
    }));

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, []);

    const handleDelete = (tag: string) => {
      const newArr = value.filter((i) => i !== tag);
      onChange(newArr);

      message.destroy();

      // Focus the input after deleting a tag
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setContent(e.target.value);

      onTextContentChange(e.target.value);
    };

    const handleBlur = () => {
      if (currentInputValue) {
        if (value.includes(currentInputValue)) {
          void messageApi.error('This email is already on the invitation list.');
          setContent('');
          onTextContentChange('');
          return;
        }
        onChange([...value, currentInputValue]);
        setContent('');
        onTextContentChange('');
      }
    };

    // 避免触发表单提交
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // console.log('e.key')
      // console.log(e.key)
      return e.key === 'Enter' || (e.key === ' ' && e.preventDefault());
    };

    const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || (e.key === ' ' && currentInputValue)) {
        if (value.includes(currentInputValue)) {
          void messageApi.error('This email is already on the invitation list.');
          setContent('');
          onTextContentChange('');
          return;
        }
        onChange([...value, currentInputValue]);
        // Don't auto-clear here - let parent control clearing via inputValue prop
        // This allows parent to keep input on validation failure
      }
    };

    function getTagStyle(email: string) {
      const type = emailType(email);
      switch (type) {
        case EmailType.sameDomain:
          return {
            backgroundColor: colorTokens.blue[100],
            color: colorTokens.blue[600],
          };
        case EmailType.differentDomain:
          return {
            backgroundColor: colorTokens.yellow[100],
            color: colorTokens.yellow[600],
          };
        case EmailType.orgUser:
          return {
            backgroundColor: colorTokens.neutral[300],
            color: colorTokens.brand['grey-dark'],
          };
        default:
          return {
            backgroundColor: colorTokens.blue[100],
            color: colorTokens.blue[600],
          };
      }
    }

    const tagIcon = (email: string) => {
      const type = emailType(email);
      switch (type) {
        case EmailType.sameDomain:
          return <OrganizationIcon />;
        case EmailType.differentDomain:
          return <WorldIcon />;
        case EmailType.orgUser:
          return <Avatar size={16}>{getUserInitials(email)}</Avatar>;
        default:
          return null;
      }
    };

    return (
      <>
        {contextHolder}
        <div>
          <div
            className={'border-neutral-400 bg-brand-white rounded border-[1px] border-solid p-1 flex flex-wrap items-center gap-1'}
            onClick={() => inputRef.current?.focus()}
            {...props}
          >
            {value.map((item) =>
              item.length > 50 ? (
                <CustomTooltip title={item} key={item}>
                  <Tag
                    className="inline-flex items-center space-x-1 m-0"
                    icon={tagIcon(item)}
                    style={getTagStyle(item)}
                    bordered={false}
                    closable
                    onClose={() => handleDelete(item)}
                  >
                    {`${item.slice(0, 50)}...`}
                  </Tag>
                </CustomTooltip>
              ) : (
                <Tag
                  className="inline-flex items-center space-x-1 m-0"
                  icon={tagIcon(item)}
                  style={getTagStyle(item)}
                  bordered={false}
                  closable
                  key={item}
                  onClose={() => handleDelete(item)}
                >
                  {item}
                </Tag>
              )
            )}
            <Input
              size="small"
              ref={inputRef}
              bordered={false}
              placeholder={!value.length ? placeholder : ''}
              className={'w-full bg-transparent'}
              value={currentInputValue}
              onChange={handleChange}
              // onBlur={handleBlur}
              // onKeyPress={handleKeyPress}
              onKeyDown={handleKeyPress}
              onKeyUp={handleKeyUp}
              autoFocus={true}
            />
          </div>
        </div>
      </>
    );
  }
);

TagInput.displayName = 'TagInput';

export default TagInput;
